import { useEffect, useState } from 'react';
import { imagesService } from '../services/api';
import { formatApptDateLong } from '../utils/dateFormatters';
import Lightbox from '../components/Lightbox/Lightbox';
import './Gallery.css';

const KIND_LABEL = { customer: 'From the client', before: 'Before', after: 'After' };

// Photo library across all jobs — the raw material for the website's
// before/after sections. View full size or download straight from here.
export default function Gallery() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pairs'); // pairs | all
  const [lightbox, setLightbox] = useState(null); // { images: [{src,label}], index }

  useEffect(() => {
    imagesService.gallery().then((r) => {
      if (r.success) setJobs(r.jobs || []);
      setLoading(false);
    });
  }, []);

  const visible = jobs.filter((j) => {
    if (filter === 'all') return true;
    const kinds = new Set(j.images.map((i) => i.kind));
    return kinds.has('before') && kinds.has('after');
  });

  return (
    <div className="gallery-page">
      <div className="page-header">
        <h1 className="page-title">Photo Gallery</h1>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="pairs">Before &amp; after pairs</option>
          <option value="all">All jobs with photos</option>
        </select>
      </div>

      {loading ? (
        <div className="empty-state"><div className="loading-spinner"></div></div>
      ) : visible.length === 0 ? (
        <div className="empty-state">
          <p>{filter === 'pairs'
            ? 'No before & after pairs yet — add both kinds to a job from its appointment.'
            : 'No job photos yet — clients can attach them when booking, or add them from an appointment.'}</p>
        </div>
      ) : (
        <div className="gallery-list">
          {visible.map((job) => {
            // Flat view order for the lightbox — stepping through a pair goes
            // straight from Before to After.
            const viewList = ['before', 'after', 'customer'].flatMap((kind) =>
              job.images.filter((i) => i.kind === kind).map((img) => ({
                id: img.id,
                src: imagesService.fileUrl(img.id),
                label: `${KIND_LABEL[kind]} — ${job.service_name || 'Job'}`
              }))
            );
            return (
              <div key={job.appointment_id} className="gallery-card">
                <div className="gallery-card-head">
                  <h3>{job.service_name || 'Job'}</h3>
                  <p className="text-muted">
                    {[job.client_name, job.suburb].filter(Boolean).join(' · ')} · {formatApptDateLong(job.starts_at)}
                  </p>
                </div>
                {['before', 'after', 'customer'].map((kind) => {
                  const ofKind = job.images.filter((i) => i.kind === kind);
                  if (!ofKind.length) return null;
                  return (
                    <div key={kind} className="gallery-kind">
                      <span className={`gallery-kind-tag tag-${kind}`}>{KIND_LABEL[kind]}</span>
                      <div className="gallery-thumbs">
                        {ofKind.map((img) => (
                          <div key={img.id} className="gallery-thumb">
                            <button type="button" className="gallery-open"
                              onClick={() => setLightbox({ images: viewList, index: viewList.findIndex((v) => v.id === img.id) })}>
                              <img src={imagesService.fileUrl(img.id, 'thumb')} alt={`${KIND_LABEL[kind]} — ${job.service_name || 'job'}`} loading="lazy" />
                            </button>
                            <a className="gallery-download" href={imagesService.fileUrl(img.id)}
                              download={`${(job.service_name || 'job').replace(/\W+/g, '-').toLowerCase()}-${kind}-${img.id}.jpg`}>
                              ⬇ Download
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {lightbox && <Lightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />}
    </div>
  );
}
